(() => {
  const ROOT_SELECTOR = '[data-component="quark-media-text-carousel"]';
  const DESKTOP_BREAKPOINT = 1000;
  const SCROLL_SETTLE_DELAY = 200;
  const instances = new Map();

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function isFiniteNumber(value) {
    return Number.isFinite(Number(value));
  }

  class QuarkMediaTextCarousel {
    constructor(root, order) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.sourceId = root.dataset.qssSourceId || this.sectionId || String(order + 1);
      this.priority = Number.isFinite(Number(order)) ? Number(order) : 0;
      this.viewport = root.querySelector('.qmtc-viewport');
      this.items = Array.from(root.querySelectorAll('.qmtc-item'));

      this.desktopMode = root.dataset.desktopMode || 'carousel';
      this.mobileMode = root.dataset.mobileMode || 'carousel';
      this.stackOnMobile = root.dataset.stackOnMobile === 'true';
      this.snapAlign = root.dataset.snapAlign === 'start' ? 'start' : 'center';
      this.showArrowsDesktop = root.dataset.showArrowsDesktop === 'true';
      this.showArrowsMobile = root.dataset.showArrowsMobile === 'true';

      this.aapTheme = {
        '--qga-aap-bg': root.dataset.aapBg || '',
        '--qga-aap-border-color': root.dataset.aapBorder || '',
        '--qga-aap-border-width': root.dataset.aapBorderWidth || '',
        '--qga-aap-blur': root.dataset.aapBlur || '',
        '--qga-aap-text-color': root.dataset.aapTextColor || '',
        '--qga-aap-icon-bg': root.dataset.aapIconBg || ''
      };

      this.currentMode = null;
      this.currentIndex = 0;
      this.isVisible = false;
      this.visibilityRatio = 0;
      this.currentAapClearance = 0;
      this.lastAapSignature = '';
      this.anchorVisibilityRaf = null;

      this.visibilityObserver = null;
      this.isProgrammaticScroll = false;
      this.scrollLeft = 0;
      this.scrollWidth = 0;
      this.scrollContainerWidth = 0;
      this.itemOffsets = [];
      this.maxIndex = 0;
      this.closestIndex = 0;
      this.scrollSettleTimer = null;
      this.scrollMeasureRaf = null;

      this.boundKeydown = this.onKeydown.bind(this);
      this.boundResize = this.onResize.bind(this);
      this.boundSectionUnload = this.onSectionUnload.bind(this);
      this.boundBlockSelect = this.onBlockSelect.bind(this);
      this.boundAAPNavigate = this.onAAPNavigate.bind(this);
      this.boundAAPMetrics = this.onAAPMetrics.bind(this);
      this.boundScroll = this.onScroll.bind(this);
      this.boundViewportChange = this.onViewportChange.bind(this);

      this.buildVisibilityObserver();
      this.bindEvents();
      this.updateMode({ force: true });
      this.setActive(0, { syncScroll: true, userTriggered: false, emitAAP: false });
      this.emitAAPState(true);
    }

    bindEvents() {
      if (this.viewport) {
        this.viewport.addEventListener('keydown', this.boundKeydown);
      }

      window.addEventListener('resize', this.boundResize, { passive: true });
      window.addEventListener('scroll', this.boundViewportChange, { passive: true });
      document.addEventListener('shopify:section:unload', this.boundSectionUnload);
      document.addEventListener('shopify:block:select', this.boundBlockSelect);
      document.addEventListener('quark:aap:navigate', this.boundAAPNavigate);
      document.addEventListener('quark:aap:metrics', this.boundAAPMetrics);
    }

    buildVisibilityObserver() {
      if (!window.IntersectionObserver) {
        this.isVisible = true;
        this.visibilityRatio = 1;
        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target !== this.root) {
              return;
            }

            const nextRatio = clamp(Number(entry.intersectionRatio) || 0, 0, 1);
            const nextVisible = entry.isIntersecting && nextRatio > 0;
            const changed = nextVisible !== this.isVisible || Math.abs(nextRatio - this.visibilityRatio) > 0.04;

            this.isVisible = nextVisible;
            this.visibilityRatio = nextRatio;

            if (changed) {
              this.emitAAPState(false);
            }
          });
        },
        {
          threshold: [0, 0.01, 0.03, 0.1, 0.2, 0.35, 0.5, 0.75, 1]
        }
      );

      this.visibilityObserver.observe(this.root);
    }

    onKeydown(event) {
      if (!this.isCarouselActive()) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.setActive(this.currentIndex - 1, { syncScroll: true, userTriggered: true });
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.setActive(this.currentIndex + 1, { syncScroll: true, userTriggered: true });
      }
    }

    onResize() {
      this.updateMode();

      if (!this.isCarouselActive()) {
        this.scheduleAAPVisibilityEmit();
        return;
      }

      this.cacheSizeInfo();
      const maxAllowed = this.getNavigableMaxIndex();
      if (this.currentIndex > maxAllowed) {
        this.currentIndex = maxAllowed;
      }

      this.setActive(this.currentIndex, { syncScroll: false, userTriggered: false, emitAAP: false });
      this.goTo(this.currentIndex, false);
      this.scheduleAAPVisibilityEmit();
    }

    getAAPBaseBottom() {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1440;
      if (viewportWidth <= 734) {
        return 10;
      }

      return Math.round(clamp(viewportWidth * 0.018, 12, 28));
    }

    isAAPAnchorCovered() {
      const target = this.viewport || this.root;
      if (!target) {
        return false;
      }

      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      if (viewportHeight <= 0) {
        return false;
      }

      const anchorY = viewportHeight - this.getAAPBaseBottom();
      return rect.top <= anchorY && rect.bottom >= anchorY;
    }

    onViewportChange() {
      this.scheduleAAPVisibilityEmit();
    }

    scheduleAAPVisibilityEmit() {
      if (this.anchorVisibilityRaf !== null) {
        return;
      }

      this.anchorVisibilityRaf = window.requestAnimationFrame(() => {
        this.anchorVisibilityRaf = null;
        this.emitAAPState(false);
      });
    }

    onSectionUnload(event) {
      const section = event.target.closest('.shopify-section');
      if (!section) {
        return;
      }

      if (section.contains(this.root)) {
        this.destroy();
      }
    }

    onBlockSelect(event) {
      const blockId = event.detail && event.detail.blockId ? event.detail.blockId : null;
      if (!blockId) {
        return;
      }

      const index = this.items.findIndex((item) => item.dataset.shopifyBlockId === blockId);
      if (index < 0) {
        return;
      }

      this.setActive(index, { syncScroll: true, userTriggered: true });
    }

    onAAPNavigate(event) {
      const detail = event && event.detail ? event.detail : null;
      if (!detail || detail.sourceId !== this.sourceId || !this.isCarouselActive()) {
        return;
      }

      if (detail.direction === 'prev') {
        this.setActive(this.currentIndex - 1, { syncScroll: true, userTriggered: true });
      } else if (detail.direction === 'next') {
        this.setActive(this.currentIndex + 1, { syncScroll: true, userTriggered: true });
      }
    }

    onAAPMetrics(event) {
      const detail = event && event.detail ? event.detail : null;
      if (!detail || detail.sourceId !== this.sourceId) {
        if (this.currentAapClearance !== 0) {
          this.currentAapClearance = 0;
          this.root.style.setProperty('--qmtc-aap-clearance', '0px');
        }
        return;
      }

      const nextClearance = detail.visible
        ? Math.max(
          0,
          Math.round(
            (Number(detail.height) || 0)
            + (detail.docked ? 12 : (Number(detail.bottom) || 0) + 12)
          )
        )
        : 0;

      if (nextClearance === this.currentAapClearance) {
        return;
      }

      this.currentAapClearance = nextClearance;
      this.root.style.setProperty('--qmtc-aap-clearance', `${nextClearance}px`);
    }

    getModeForViewport() {
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        return this.desktopMode;
      }

      if (this.stackOnMobile) {
        return 'stack';
      }

      return this.mobileMode;
    }

    isAapArrowEnabledForViewport() {
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        return this.showArrowsDesktop;
      }

      return this.showArrowsMobile;
    }

    isCarouselActive() {
      return this.currentMode === 'carousel' && this.items.length > 1;
    }

    updateMode({ force = false } = {}) {
      const nextMode = this.getModeForViewport();
      if (!force && nextMode === this.currentMode) {
        return;
      }

      this.currentMode = nextMode;
      this.root.dataset.mode = nextMode;
      this.root.dataset.carouselActive = this.isCarouselActive() ? 'true' : 'false';

      if (this.viewport) {
        this.viewport.setAttribute('tabindex', this.isCarouselActive() ? '0' : '-1');
      }

      this.detachScrollHandlers();

      if (!this.isCarouselActive()) {
        this.itemOffsets = [];
        this.maxIndex = Math.max(0, this.items.length - 1);
        this.emitAAPState(false);
        return;
      }

      this.cacheSizeInfo();

      const maxAllowed = this.getNavigableMaxIndex();
      if (this.currentIndex > maxAllowed) {
        this.currentIndex = maxAllowed;
      }

      this.setActive(this.currentIndex, { syncScroll: false, userTriggered: false, emitAAP: false });
      this.goTo(this.currentIndex, false);
      this.closestIndex = this.currentIndex;
      this.attachScrollHandlers();
      this.emitAAPState(false);
    }

    attachScrollHandlers() {
      if (!this.viewport) {
        return;
      }

      this.viewport.removeEventListener('scroll', this.boundScroll);
      this.viewport.addEventListener('scroll', this.boundScroll, { passive: true });
    }

    detachScrollHandlers() {
      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.boundScroll);
      }

      if (this.scrollSettleTimer) {
        window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = null;
      }

      if (this.scrollMeasureRaf) {
        window.cancelAnimationFrame(this.scrollMeasureRaf);
        this.scrollMeasureRaf = null;
      }

      this.isProgrammaticScroll = false;
    }

    onScroll() {
      if (!this.viewport || !this.isCarouselActive()) {
        return;
      }

      if (!this.scrollMeasureRaf) {
        this.scrollMeasureRaf = window.requestAnimationFrame(() => {
          this.scrollMeasureRaf = null;
          if (!this.viewport) {
            return;
          }

          this.scrollLeft = this.viewport.scrollLeft;

          if (this.isProgrammaticScroll) {
            const candidate = this.findNearestIndexFromOffset(this.scrollLeft);
            if (candidate !== null && candidate !== this.closestIndex) {
              this.closestIndex = candidate;
              this.setActive(candidate, { syncScroll: false, userTriggered: false });
            }
          }
        });
      }

      if (this.scrollSettleTimer) {
        window.clearTimeout(this.scrollSettleTimer);
      }

      this.scrollSettleTimer = window.setTimeout(() => {
        this.setCurrentIndexFromScroll();
      }, SCROLL_SETTLE_DELAY);
    }

    setCurrentIndexFromScroll() {
      if (!this.viewport || !this.isCarouselActive()) {
        return;
      }

      this.scrollLeft = this.viewport.scrollLeft;
      this.isProgrammaticScroll = false;

      const candidate = this.findNearestIndexFromOffset(this.scrollLeft);
      if (candidate === null) {
        return;
      }

      this.closestIndex = candidate;
      this.setActive(candidate, { syncScroll: false, userTriggered: false });
    }

    cacheSizeInfo() {
      if (!this.viewport || !this.items.length) {
        this.itemOffsets = [];
        this.maxIndex = 0;
        this.scrollWidth = 0;
        this.scrollContainerWidth = 0;
        return;
      }

      this.scrollWidth = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.scrollContainerWidth = this.viewport.offsetWidth;
      this.itemOffsets = [];
      this.maxIndex = null;

      let firstAnchor = 0;

      this.items.forEach((item, itemIndex) => {
        const itemEdge = item.offsetLeft;
        if (itemIndex === 0) {
          firstAnchor = itemEdge;
        }

        let offset;
        if (this.snapAlign === 'center') {
          offset = Math.floor(item.offsetLeft - this.scrollContainerWidth / 2 + item.offsetWidth / 2);
        } else {
          offset = Math.floor(itemEdge - firstAnchor);
        }

        if (Math.abs(offset) >= Math.abs(this.scrollWidth)) {
          if (this.maxIndex === null) {
            this.maxIndex = itemIndex;
            offset = this.scrollWidth;
          } else {
            offset = null;
          }
        }

        this.itemOffsets.push(offset);
      });

      if (this.maxIndex === null) {
        this.maxIndex = this.items.length - 1;
      }
    }

    closestOffset(target, offsets) {
      return offsets.reduce((closestValue, value) => {
        if (!isFiniteNumber(value)) {
          return closestValue;
        }

        if (!isFiniteNumber(closestValue)) {
          return value;
        }

        const currentDistance = Math.abs(closestValue - target);
        const nextDistance = Math.abs(value - target);
        if (currentDistance === nextDistance) {
          return closestValue > value ? value : closestValue;
        }

        return nextDistance < currentDistance ? value : closestValue;
      });
    }

    findNearestIndexFromOffset(scrollLeft) {
      if (!this.itemOffsets.length) {
        return null;
      }

      const nearestOffset = this.closestOffset(scrollLeft, this.itemOffsets);
      if (!isFiniteNumber(nearestOffset)) {
        return null;
      }

      const nearestIndex = this.itemOffsets.findIndex((offset) => offset === nearestOffset);
      if (nearestIndex < 0) {
        return null;
      }

      return nearestIndex;
    }

    getNavigableMaxIndex() {
      if (!this.isCarouselActive()) {
        return Math.max(0, this.items.length - 1);
      }

      return Number.isFinite(Number(this.maxIndex))
        ? Number(this.maxIndex)
        : Math.max(0, this.items.length - 1);
    }

    setActive(index, options = {}) {
      const opts = {
        syncScroll: true,
        userTriggered: false,
        emitAAP: true,
        ...options
      };

      if (!this.items.length) {
        return;
      }

      const nextIndex = Number(index);
      const maxAllowed = this.isCarouselActive() ? this.getNavigableMaxIndex() : this.items.length - 1;

      if (!Number.isFinite(nextIndex) || nextIndex < 0 || nextIndex > maxAllowed) {
        return;
      }

      const previousIndex = this.currentIndex;
      this.currentIndex = nextIndex;

      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-current', itemIndex === this.currentIndex);
      });

      if (opts.syncScroll && this.isCarouselActive()) {
        this.goTo(this.currentIndex, true);
      }

      if (previousIndex !== this.currentIndex) {
        this.root.dispatchEvent(
          new CustomEvent('qmtc:slide-change', {
            detail: {
              index: this.currentIndex,
              userTriggered: opts.userTriggered
            }
          })
        );
      }

      if (opts.emitAAP) {
        this.emitAAPState(false);
      }
    }

    goTo(index, smooth) {
      if (!this.viewport || !this.isCarouselActive()) {
        return;
      }

      const left = this.getScrollLeftForIndex(index);
      if (!isFiniteNumber(left)) {
        return;
      }

      this.isProgrammaticScroll = Boolean(smooth);

      this.viewport.scrollTo({
        left,
        behavior: smooth ? 'smooth' : 'auto'
      });

      if (!smooth) {
        this.scrollLeft = this.viewport.scrollLeft;
      }
    }

    getScrollLeftForIndex(index) {
      if (!this.viewport) {
        return null;
      }

      if (!this.itemOffsets.length) {
        this.cacheSizeInfo();
      }

      const nextIndex = Number(index);
      const fallbackIndex = this.getNavigableMaxIndex();
      const offset = this.itemOffsets[nextIndex];
      const fallbackOffset = this.itemOffsets[fallbackIndex];
      const rawLeft = isFiniteNumber(offset) ? Number(offset) : Number(fallbackOffset || 0);
      const maxLeft = Math.max(0, this.scrollWidth);

      return clamp(rawLeft, 0, maxLeft);
    }

    emitAAPState(force) {
      const navEnabled = this.isCarouselActive() && this.isAapArrowEnabledForViewport();
      const maxAllowed = this.isCarouselActive() ? this.getNavigableMaxIndex() : Math.max(0, this.items.length - 1);
      const anchorCovered = this.isAAPAnchorCovered();

      const payload = {
        sourceId: this.sourceId,
        sourceEl: this.root,
        visible: this.isVisible && navEnabled && anchorCovered,
        priority: this.priority,
        ratio: this.visibilityRatio,
        panelIndex: this.currentIndex,
        dockToSource: true,
        dockTargetEl: this.viewport || this.root,
        ctaLabel: '',
        modalKey: '',
        navEnabled,
        navPrevDisabled: this.currentIndex <= 0,
        navNextDisabled: this.currentIndex >= maxAllowed,
        aapTheme: this.aapTheme
      };

      const signature = JSON.stringify([
        payload.sourceId,
        payload.visible,
        payload.panelIndex,
        payload.navEnabled,
        payload.navPrevDisabled,
        payload.navNextDisabled,
        Math.round(payload.ratio * 100)
      ]);

      if (!force && signature === this.lastAapSignature) {
        return;
      }

      this.lastAapSignature = signature;
      document.dispatchEvent(new CustomEvent('quark:aap:state', { detail: payload }));
    }

    destroy() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }

      this.detachScrollHandlers();

      if (this.viewport) {
        this.viewport.removeEventListener('keydown', this.boundKeydown);
      }

      window.removeEventListener('resize', this.boundResize);
      window.removeEventListener('scroll', this.boundViewportChange);
      document.removeEventListener('shopify:section:unload', this.boundSectionUnload);
      document.removeEventListener('shopify:block:select', this.boundBlockSelect);
      document.removeEventListener('quark:aap:navigate', this.boundAAPNavigate);
      document.removeEventListener('quark:aap:metrics', this.boundAAPMetrics);

      if (this.anchorVisibilityRaf !== null) {
        window.cancelAnimationFrame(this.anchorVisibilityRaf);
        this.anchorVisibilityRaf = null;
      }

      this.root.style.setProperty('--qmtc-aap-clearance', '0px');

      document.dispatchEvent(new CustomEvent('quark:aap:state', {
        detail: {
          sourceId: this.sourceId,
          sourceEl: this.root,
          removed: true,
          visible: false,
          priority: this.priority,
          ratio: 0,
          panelIndex: this.currentIndex,
          dockToSource: false,
          dockTargetEl: null,
          ctaLabel: '',
          modalKey: '',
          navEnabled: false,
          navPrevDisabled: true,
          navNextDisabled: true,
          aapTheme: this.aapTheme
        }
      }));

      instances.delete(this.root);
    }
  }

  function initOne(root) {
    if (!root || instances.has(root)) {
      return;
    }

    const allAapSources = Array.from(document.querySelectorAll('[data-qss-source-id]'));
    const sourceOrder = Math.max(0, allAapSources.indexOf(root));
    instances.set(root, new QuarkMediaTextCarousel(root, sourceOrder));
  }

  function initAll(container) {
    const scope = container || document;
    const roots = scope.matches && scope.matches(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => initOne(root));
  }

  function unloadWithin(container) {
    const roots = container.matches && container.matches(ROOT_SELECTOR)
      ? [container]
      : Array.from(container.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) {
      initAll(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target) {
      unloadWithin(event.target);
    }
  });
})();
